import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { type PixelType } from '@/api/pixel';
import Edit from '@/assets/svg/edit.svg?react';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { MoreActionsCell } from '@/components/MoreActionsCell';
import { ColorBadge, Link } from '@/components/typography';
import { colorWithOpacity } from '@/constants/colors';
import { USDateViewFormat } from '@/constants/formats';
import { PIXEL_COMPARE_MAX_LIMIT, PIXEL_GROUP_MAX_LIMIT } from '@/constants/numbers';
import type SolAPIError from '@/error/SolAPIError';
import { numberFormat } from '@/helper/numberFormatter';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';
import { COLOR_GREEN_400, COLOR_MANGO } from '@/styles/palette';

import PixelTableActionContent from './PixelTableActionContent';

type Props = {
  data?: PixelType[];
  error: SolAPIError | null;
  totalResults?: number;
  isLoading?: boolean;
};

enum ActionKey {
  EDIT = 'EDIT',
}

const PixelsListTable = ({ data, error, totalResults, isLoading = false }: Props) => {
  const user = useMe();
  const [selectedPixels, setSelectedPixels] = useState<PixelType[]>([]);

  const sortFields = ROUTES.pixels.searchParams['p.sortBy'].enumValues;
  const maxSelectablePixels = Math.max(PIXEL_GROUP_MAX_LIMIT, PIXEL_COMPARE_MAX_LIMIT);
  const isMaxCompareLimit = selectedPixels.length === maxSelectablePixels;
  const selectedPixelIds = useMemo(() => selectedPixels.map((pixel) => pixel.id), [selectedPixels]);
  const selectedPixelIdSet = useMemo(() => new Set(selectedPixelIds), [selectedPixelIds]);
  const selectedPixelById = useMemo(
    () => new Map(selectedPixels.map((pixel) => [pixel.id, pixel])),
    [selectedPixels],
  );
  const visiblePixelById = useMemo(
    () => new Map((data ?? []).map((pixel) => [pixel.id, pixel])),
    [data],
  );

  const handleSelectRow = (pixel: PixelType) => {
    const isPixelSelected = selectedPixelIds.includes(pixel.id);

    if (!isPixelSelected && isMaxCompareLimit) {
      return;
    }

    if (isPixelSelected) {
      setSelectedPixels((previousPixels) =>
        previousPixels.filter((selectedPixel) => selectedPixel.id !== pixel.id),
      );
      return;
    }

    setSelectedPixels((previousPixels) => [...previousPixels, pixel]);
  };

  const handleRemoveSelectedPixel = (pixel: PixelType) => {
    setSelectedPixels((previousPixels) =>
      previousPixels.filter((selectedPixel) => selectedPixel.id !== pixel.id),
    );
  };

  const handleSelectedPixelIdsChange = (selectedRowIds: string[]) => {
    const nextSelectedPixels = selectedRowIds.flatMap((pixelId) => {
      const existingPixel = selectedPixelById.get(pixelId);
      if (existingPixel != null) {
        return [existingPixel];
      }

      const visiblePixel = visiblePixelById.get(pixelId);
      return visiblePixel != null ? [visiblePixel] : [];
    });

    setSelectedPixels(nextSelectedPixels.slice(0, maxSelectablePixels));
  };

  const columns = useMemo<InstantSearchDataTableColumnDef<PixelType>[]>(
    () => [
      {
        header: t`Pixel Name`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: sortFields.includes('name'),
        minSize: 160,
        flex: 1,
        cell: ({ row }) => (
          <Link variant="caption1" to={ROUTES.pixelById(row.original.id)}>
            {row.original.name}
          </Link>
        ),
      },
      {
        header: t`Channel`,
        id: 'channel.name',
        accessorKey: 'channel.name',
        enableSorting: sortFields.includes('channel.name'),
        minSize: 120,
        flex: 1,
        cell: ({ row }) => row.original.channel.name,
      },
      {
        header: t`Vendor`,
        id: 'vendor.name',
        accessorKey: 'vendor.name',
        enableSorting: sortFields.includes('vendor.name'),
        minSize: 120,
        flex: 1,
        cell: ({ row }) => row.original.vendor.name,
      },
      {
        header: t`Audience`,
        id: 'audience.name',
        accessorKey: 'audience.name',
        enableSorting: sortFields.includes('audience.name'),
        minSize: 140,
        flex: 1,
        cell: ({ row }) => row.original.audience.name,
      },
      {
        header: t`Status`,
        id: 'status',
        accessorKey: 'status',
        enableSorting: sortFields.includes('status'),
        size: 100,
        cell: ({ row }) =>
          row.original.status === 'paused' ? (
            <ColorBadge color={colorWithOpacity(COLOR_MANGO, 0.3)}>
              <Trans>Paused</Trans>
            </ColorBadge>
          ) : (
            <ColorBadge color={colorWithOpacity(COLOR_GREEN_400, 0.3)}>
              <Trans>Active</Trans>
            </ColorBadge>
          ),
      },
      {
        header: t`Updated On`,
        id: 'updated_at',
        accessorKey: 'updated_at',
        enableSorting: sortFields.includes('updated_at'),
        size: 120,
        meta: { align: 'end' },
        cell: ({ row }) => dayjs(row.original.updated_at).format(USDateViewFormat),
      },
      {
        header: t`Last Impression`,
        id: 'metrics.lastImpressionDate',
        accessorKey: 'metrics.lastImpressionDate',
        enableSorting: sortFields.includes('metrics.lastImpressionDate'),
        size: 145,
        meta: { align: 'end' },
        cell: ({ row }) =>
          row.original.metrics?.lastImpressionDate
            ? dayjs(row.original.metrics.lastImpressionDate).format(USDateViewFormat)
            : '',
      },
      {
        header: t`Impressions (Last 30d)`,
        id: 'metrics.impressions',
        accessorKey: 'metrics.impressions',
        enableSorting: sortFields.includes('metrics.impressions'),
        size: 120,
        meta: { align: 'end' },
        cell: ({ row }) => numberFormat(row.original.metrics?.impressions),
      },
      {
        header: '',
        id: 'action',
        size: 48,
        meta: { align: 'end' },
        hidden: !user?.isAdmin,
        enableSorting: false,
        cell: ({ row }) => (
          <MoreActionsCell<ActionKey>
            menuItems={[
              {
                key: ActionKey.EDIT,
                label: t`Edit`,
                icon: <Edit />,
                to: ROUTES.pixelEditById(row.original.id),
              },
            ]}
          />
        ),
      },
    ],
    [sortFields, user?.isAdmin],
  );

  return (
    <div data-testid="pixels-table">
      <InstantSearchDataTable<PixelType>
        actionContent={
          <PixelTableActionContent
            selectedPixels={selectedPixels}
            onRemoveSelectedPixel={handleRemoveSelectedPixel}
          />
        }
        columns={columns}
        data={data}
        totalResults={totalResults}
        loading={isLoading}
        emptyMessage={<Trans>No pixels found.</Trans>}
        error={error}
        getRowId={(record) => record.id}
        initialColumnPinning={{
          left: ['name'],
          right: ['action'],
        }}
        rowSelection={{
          selectedRowIds: selectedPixelIds,
          onSelectedRowIdsChange: handleSelectedPixelIdsChange,
          getRowCanSelect: (row) => selectedPixelIdSet.has(row.original.id) || !isMaxCompareLimit,
          enableSelectAll: true,
          columnWidth: 40,
        }}
        onRow={(record) => ({
          onClick: () => handleSelectRow(record),
        })}
      />
    </div>
  );
};

export default PixelsListTable;
